
import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, Download, Trash2, RefreshCw, ArrowRight, Pencil, History } from "lucide-react";
import {
  OrcamentoCorrigirLinhaDialog,
  type OrcamentoLinhaParaCorrigir,
} from "@/components/orcamento/OrcamentoCorrigirLinhaDialog";
import { OrcamentoHistoricoAlteracoes } from "@/components/orcamento/OrcamentoHistoricoAlteracoes";
import { useSupabase } from "@/hooks/useSupabase";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import * as XLSX from "xlsx";
import { parseExcelDate as parseDate, parseExcelNumber as parseNumber } from "@/lib/excelImportParsers";

interface OrcamentoItem {
  id: string;
  mes: string | null;
  mes_competencia: string | null;
  data: string | null;
  valor_imposto: number | null;
  valor: number;
  historico: string | null;
  centro_resultado: string | null;
  tipo_receita_despesa: string | null;
  classificacao_receita_despesa: string | null;
  descricao_receita_despesa: string | null;
  cliente_fornecedor: string | null;
  pti: string | null;
  tipo_td: string | null;
  classificacao_td: string | null;
  area: string | null;
  sub_area: string | null;
  created_at: string;
}

// Mapeamento de colunas do banco (inclui colunas de gestão e nova estrutura)
const DB_COLUMNS = [
  { key: "mes", label: "Mês", required: false },
  { key: "mes_competencia", label: "Mês Competência", required: false },
  { key: "data", label: "Data", required: false },
  { key: "valor_imposto", label: "Valor Imposto", required: false },
  { key: "valor", label: "Valor (R$)", required: true },
  { key: "historico", label: "Histórico", required: false },
  { key: "centro_resultado", label: "Centro de Resultado", required: false },
  { key: "tipo_receita_despesa", label: "Tipo Receita/Despesa", required: false },
  { key: "classificacao_receita_despesa", label: "Classificação Receita/Despesa", required: false },
  { key: "descricao_receita_despesa", label: "Descrição Receita/Despesa", required: false },
  { key: "cliente_fornecedor", label: "Cliente/Fornecedor", required: false },
  { key: "pti", label: "P/T.I.", required: false },
  { key: "tipo_td", label: "Tipo TD", required: false },
  { key: "classificacao_td", label: "Classificação TD", required: false },
  { key: "area", label: "AREA (Área)", required: false },
  { key: "sub_area", label: "SUB AREA (Subárea)", required: false },
];

export default function OrcamentoPage() {
  const { supabase } = useSupabase();
  const [items, setItems] = useState<OrcamentoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [excelData, setExcelData] = useState<any[][]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [corrigirOpen, setCorrigirOpen] = useState(false);
  const [itemParaCorrigir, setItemParaCorrigir] = useState<OrcamentoLinhaParaCorrigir | null>(null);
  const [historicoRegistroId, setHistoricoRegistroId] = useState<string | null>(null);
  const [historicoRefresh, setHistoricoRefresh] = useState(0);

  // State for year management
  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const selectedYearRef = React.useRef(selectedYear);
  selectedYearRef.current = selectedYear;

  const fetchYears = useCallback(async () => {
    try {
      const { data: rawData, error: rawError } = await supabase
        .from("solis_orcamento")
        .select("mes");

      if (rawError) throw rawError;

      const uniqueYears = Array.from(new Set(
        rawData?.map(item => item.mes?.split("-")[0]).filter(Boolean) as string[]
      )).sort().reverse();

      setYears(uniqueYears);
      if (uniqueYears.length > 0 && !selectedYearRef.current) {
        setSelectedYear(uniqueYears[0]);
      }
    } catch (error: any) {
      console.error("Erro ao buscar anos:", error);
      toast.error("Erro ao carregar anos do orçamento. Tente atualizar a página.");
    }
  }, [supabase]);

  const fetchItems = useCallback(async (year: string) => {
    setLoading(true);
    try {
      const all: OrcamentoItem[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const to = from + pageSize - 1;
        const { data, error } = await supabase
          .from("solis_orcamento")
          .select("*")
          .ilike("mes", `${year}-%`)
          .order("data", { ascending: false })
          .range(from, to);

        if (error) throw error;
        const page = data || [];
        all.push(...page);
        hasMore = page.length === pageSize;
        from += pageSize;
      }

      setItems(all);
    } catch (error: any) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao carregar dados do orçamento. Tente atualizar.");
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  useEffect(() => {
    if (selectedYear) {
      fetchItems(selectedYear);
    } else {
      setItems([]);
    }
  }, [selectedYear, fetchItems]);

  const handleRefetch = useCallback(() => {
    if (selectedYearRef.current) {
      fetchItems(selectedYearRef.current);
    } else {
      fetchYears();
    }
  }, [fetchYears, fetchItems]);

  useRefetchOnFocus(handleRefetch);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar se é XLSX ou XLS
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Por favor, selecione um arquivo Excel (.xlsx ou .xls)");
      return;
    }

    try {
      // Ler o arquivo Excel
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      // Pegar a primeira planilha (sheet)
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Converter para JSON (array de arrays)
      // raw: true mantém os valores como estão (datas como números serial)
      // raw: false converte datas para Date, mas pode perder formatação
      // Vamos usar raw: true e tratar manualmente
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1, // Usar array de arrays
        defval: null, // Valores padrão como null
        raw: true, // Manter valores brutos para processar manualmente
      }) as any[][];

      // Tentar também com raw: false para pegar datas convertidas
      const jsonDataWithDates = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: null,
        raw: false, // Converte datas para Date
      }) as any[][];

      // Mesclar: usar valores de jsonDataWithDates para células que são Date
      for (let i = 0; i < jsonData.length; i++) {
        if (jsonDataWithDates[i]) {
          for (let j = 0; j < jsonData[i].length; j++) {
            if (jsonDataWithDates[i][j] instanceof Date) {
              jsonData[i][j] = jsonDataWithDates[i][j];
            }
          }
        }
      }

      if (jsonData.length < 2) {
        toast.error("A planilha está vazia ou não tem dados");
        return;
      }

      // Primeira linha são os cabeçalhos
      // Tentar detectar se a primeira linha realmente contém cabeçalhos
      // Se a maioria dos valores parecer numérico ou data, pode não ser cabeçalho
      const firstRow = jsonData[0] || [];
      const isLikelyHeader = firstRow.some((cell: any) => {
        if (!cell) return false;
        const str = String(cell).trim();
        // Se contém texto (não só números), provavelmente é cabeçalho
        return /[a-zA-Z]/.test(str) && str.length > 0;
      });

      const headers = firstRow.map((h, idx) => {
        if (h && String(h).trim()) {
          const header = String(h).trim();
          // Se parecer um número ou data, não usar como cabeçalho
          if (!isLikelyHeader || /^-?\d+([.,]\d+)?$/.test(header)) {
            return `Coluna ${idx + 1}`;
          }
          return header;
        }
        return `Coluna ${idx + 1}`;
      });

      // Resto são os dados
      const dataRows = jsonData.slice(1);

      setExcelHeaders(headers);
      setExcelData(dataRows);

      // Tentar mapeamento automático baseado nos nomes das colunas
      const autoMapping: Record<string, string> = {};
      headers.forEach((header, idx) => {
        const headerLower = header.toLowerCase();
        const normalizedHeader = headerLower
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        // Tentar encontrar correspondência automática
        for (const dbCol of DB_COLUMNS) {
          const dbLabelLower = dbCol.label.toLowerCase();
          if (
            headerLower.includes(dbLabelLower) ||
            headerLower.includes(dbCol.key.replace(/_/g, " ")) ||
            (headerLower.includes("mês") && dbCol.key === "mes") ||
            (headerLower.includes("competência") && dbCol.key === "mes_competencia") ||
            (headerLower.includes("vlr") && dbCol.key === "valor_imposto") ||
            (headerLower.includes("valor") && !headerLower.includes("imposto") && dbCol.key === "valor") ||
            ((normalizedHeader === "p t i" || normalizedHeader === "pti") && dbCol.key === "pti") ||
            (normalizedHeader.includes("tipo td") && dbCol.key === "tipo_td") ||
            (normalizedHeader.includes("classificacao td") && dbCol.key === "classificacao_td") ||
            (headerLower.includes("sub") && headerLower.includes("area") && dbCol.key === "sub_area") ||
            (headerLower.includes("area") && !headerLower.includes("sub") && dbCol.key === "area")
          ) {
            autoMapping[header] = dbCol.key;
            break;
          }
        }
      });

      setColumnMapping(autoMapping);
      setMappingDialogOpen(true);
    } catch (error: any) {
      console.error("Erro ao ler arquivo:", error);
      toast.error(`Erro ao ler arquivo: ${error.message}`);
    } finally {
      // Limpar input
      event.target.value = "";
    }
  };

  const handleImportWithMapping = async () => {
    // Validar se a coluna obrigatória (valor) está mapeada
    const valorMapped = Object.values(columnMapping).includes("valor");
    if (!valorMapped) {
      toast.error("Por favor, mapeie a coluna 'Valor (R$)' que é obrigatória");
      return;
    }

    setUploading(true);
    setImportProgress(0);
    setImportStatus("Processando planilha...");
    setMappingDialogOpen(false);

    try {
      const records: any[] = [];
      const yearsFound = new Set<string>();

      for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
        const row = excelData[rowIndex];
        // Pular linhas vazias
        if (!row || row.length === 0 || row.every(cell => !cell)) continue;

        const record: any = {};

        // Mapear cada coluna do banco
        for (const dbCol of DB_COLUMNS) {
          // Encontrar qual coluna do Excel está mapeada para esta coluna do banco
          const excelHeader = Object.keys(columnMapping).find(
            h => columnMapping[h] === dbCol.key
          );

          if (excelHeader) {
            const excelIndex = excelHeaders.indexOf(excelHeader);
            if (excelIndex >= 0 && excelIndex < row.length) {
              const value = row[excelIndex];

              // Processar baseado no tipo
              if (dbCol.key === "data") {
                const parsedDate = parseDate(value);
                // Log para debug (remover depois)
                if (!parsedDate && value) {
                  console.log("Data não parseada:", value, typeof value);
                }
                record[dbCol.key] = parsedDate;
              } else if (dbCol.key === "valor" || dbCol.key === "valor_imposto") {
                record[dbCol.key] = parseNumber(value) || (dbCol.key === "valor" ? 0 : null);
              } else {
                record[dbCol.key] = value ? String(value).trim() : null;
              }
            } else {
              record[dbCol.key] = dbCol.key === "valor" ? 0 : null;
            }
          } else {
            record[dbCol.key] = dbCol.key === "valor" ? 0 : null;
          }
        }

        // Importar todas as linhas (incluindo valor 0 ou vazio)
        records.push(record);
        if (record.mes && record.mes.length >= 4) {
          yearsFound.add(record.mes.substring(0, 4));
        } else if (record.data) {
          yearsFound.add(record.data.substring(0, 4));
        }

        // Evita bloquear a UI em planilhas grandes
        if (rowIndex % 300 === 0) {
          setImportProgress(Math.min(60, Math.round(((rowIndex + 1) / Math.max(excelData.length, 1)) * 60)));
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      if (records.length === 0) {
        toast.error("Nenhum registro válido encontrado no arquivo");
        setUploading(false);
        setImportStatus("");
        setImportProgress(0);
        return;
      }

      // Inserir em lote com concorrência limitada para acelerar importação
      const batchSize = 500;
      const maxConcurrentBatches = 3;
      const totalBatches = Math.ceil(records.length / batchSize);

      for (let i = 0; i < totalBatches; i += maxConcurrentBatches) {
        const groupPromises: Promise<void>[] = [];

        for (let j = i; j < Math.min(i + maxConcurrentBatches, totalBatches); j++) {
          const start = j * batchSize;
          const end = start + batchSize;
          const batch = records.slice(start, end);

          groupPromises.push((async () => {
            const { error } = await supabase
              .from("solis_orcamento")
              .insert(batch);

            if (error) throw new Error(`Lote ${j + 1}/${totalBatches}: ${error.message}`);
          })());
        }

        setImportStatus(`Enviando lotes ${i + 1} a ${Math.min(i + maxConcurrentBatches, totalBatches)} de ${totalBatches}...`);
        await Promise.all(groupPromises);
        const uploadedBatches = Math.min(i + maxConcurrentBatches, totalBatches);
        setImportProgress(60 + Math.round((uploadedBatches / totalBatches) * 40));
      }

      toast.success(`${records.length} registros importados com sucesso!`);

      // Refresh years and items
      await fetchYears();

      // If we found a year in the import, switch to it, otherwise keep current
      const importedYear = Array.from(yearsFound).sort().reverse()[0];
      if (importedYear) {
        setSelectedYear(importedYear);
        // The useEffect will handle fetching items
      } else if (selectedYear) {
        fetchItems(selectedYear);
      }

    } catch (error: any) {
      console.error("Erro ao importar:", error);
      toast.error(`Erro ao importar arquivo: ${error.message}`);
    } finally {
      setUploading(false);
      setImportStatus("");
      setImportProgress(0);
    }
  };

  const handleDeleteYear = async () => {
    if (!selectedYear) return;

    if (!confirm(`Tem certeza que deseja excluir TODOS os registros de ${selectedYear}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("solis_orcamento")
        .delete()
        .ilike("mes", `${selectedYear}-%`);

      if (error) throw error;
      toast.success(`Registros de ${selectedYear} excluídos com sucesso`);

      // Update lists
      await fetchYears();
      const newItems = items.filter(i => !i.mes?.startsWith(selectedYear));
      setItems(newItems);

    } catch (error: any) {
      console.error("Erro ao excluir:", error);
      toast.error("Erro ao excluir registros");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  // Calcular totais
  const totalValor = items.reduce((sum, item) => sum + (item.valor || 0), 0);
  const totalItems = items.length;

  return (
    <div className="space-y-4 p-6 max-w-7xl w-full mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Orçamento</h2>
          <p className="text-muted-foreground text-sm">
            Gerencie e visualize os dados de orçamento previsto importados da planilha.
          </p>
        </div>
        <div className="flex gap-2">
          {/* Year Filter */}
          <div className="w-[120px]">
            {years.length > 0 && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <Button
            variant="outline"
            onClick={() => selectedYear && fetchItems(selectedYear)}
            disabled={loading || !selectedYear}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <label htmlFor="file-upload">
            <Button asChild variant="default" disabled={uploading}>
              <span>
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? `Importando... ${importProgress}%` : "Importar Excel"}
              </span>
            </Button>
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />

          {/* Dialog de mapeamento de colunas */}
          <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Mapear Colunas da Planilha</DialogTitle>
                <DialogDescription>
                  Selecione qual coluna da planilha Excel corresponde a cada campo do banco de dados.
                  A coluna "Valor (R$)" é obrigatória.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {DB_COLUMNS.map((dbCol) => (
                  <div key={dbCol.key} className="flex items-center gap-4">
                    <div className="w-64 flex-shrink-0">
                      <Label className="flex items-center gap-2">
                        {dbCol.label}
                        {dbCol.required && (
                          <span className="text-red-500 text-xs">*</span>
                        )}
                      </Label>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1">
                      <Select
                        value={Object.keys(columnMapping).find(h => columnMapping[h] === dbCol.key) || "__unmapped__"}
                        onValueChange={(value) => {
                          // Remover mapeamento anterior desta coluna do banco
                          const newMapping = { ...columnMapping };
                          const oldKey = Object.keys(newMapping).find(k => newMapping[k] === dbCol.key);
                          if (oldKey) delete newMapping[oldKey];

                          // Se não for "não mapear", adicionar novo mapeamento
                          if (value && value !== "__unmapped__") {
                            // Remover mapeamento da coluna do Excel selecionada (se já estava mapeada)
                            if (newMapping[value]) {
                              delete newMapping[value];
                            }
                            newMapping[value] = dbCol.key;
                          }

                          setColumnMapping(newMapping);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a coluna da planilha..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unmapped__">-- Não mapear --</SelectItem>
                          {excelHeaders.map((header, idx) => (
                            <SelectItem key={idx} value={header}>
                              {header} (Coluna {idx + 1})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setMappingDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleImportWithMapping}
                  disabled={!Object.values(columnMapping).includes("valor")}
                >
                  Importar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {items.length > 0 && selectedYear && (
            <Button
              variant="destructive"
              onClick={handleDeleteYear}
              disabled={loading}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Limpar {selectedYear}
            </Button>
          )}
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Registros ({selectedYear || "Geral"})</CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalItems}</div>
            <p className="text-xs text-muted-foreground">Itens importados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Total ({selectedYear || "Geral"})</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalValor)}</div>
            <p className="text-xs text-muted-foreground">Soma de todos os valores</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Última Atualização</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {items.length > 0
                ? formatDate(items[0]?.created_at || null)
                : "-"}
            </div>
            <p className="text-xs text-muted-foreground">Data do último registro</p>
          </CardContent>
        </Card>
      </div>

      {uploading && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{importStatus || "Importando..."}</span>
                <span className="font-medium">{importProgress}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela de dados */}
      <Card>
        <CardHeader>
          <CardTitle>Registros de Orçamento</CardTitle>
          <CardDescription>
            Visualize e gerencie os dados importados da planilha
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {years.length > 0
                ? "Nenhum registro encontrado para este ano."
                : "Nenhum registro encontrado. Importe um arquivo Excel (.xlsx) para começar."}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mês</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Histórico</TableHead>
                    <TableHead>Centro de Resultado</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Classificação</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>P/T.I.</TableHead>
                    <TableHead>Tipo TD</TableHead>
                    <TableHead>Classificação TD</TableHead>
                    <TableHead>Área</TableHead>
                    <TableHead>Subárea</TableHead>
                    <TableHead className="w-[100px] text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.mes || "-"}</TableCell>
                      <TableCell>{formatDate(item.data)}</TableCell>
                      <TableCell className={item.valor < 0 ? "text-red-600" : "text-green-600"}>
                        {formatCurrency(item.valor)}
                      </TableCell>
                      <TableCell>{item.historico || "-"}</TableCell>
                      <TableCell>{item.centro_resultado || "-"}</TableCell>
                      <TableCell>{item.tipo_receita_despesa || "-"}</TableCell>
                      <TableCell>{item.classificacao_receita_despesa || "-"}</TableCell>
                      <TableCell>{item.cliente_fornecedor || "-"}</TableCell>
                      <TableCell>{item.pti ?? "-"}</TableCell>
                      <TableCell>{item.tipo_td ?? "-"}</TableCell>
                      <TableCell>{item.classificacao_td ?? "-"}</TableCell>
                      <TableCell>{item.area ?? "-"}</TableCell>
                      <TableCell>{item.sub_area ?? "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Corrigir linha"
                            onClick={() => {
                              setItemParaCorrigir(item);
                              setCorrigirOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Histórico desta linha"
                            onClick={() => setHistoricoRegistroId(item.id)}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedYear ? (
        <OrcamentoHistoricoAlteracoes
          selectedYear={selectedYear}
          registroIdFilter={historicoRegistroId}
          onClearRegistroFilter={() => setHistoricoRegistroId(null)}
          refreshToken={historicoRefresh}
        />
      ) : null}

      <OrcamentoCorrigirLinhaDialog
        open={corrigirOpen}
        onOpenChange={setCorrigirOpen}
        item={itemParaCorrigir}
        onSuccess={() => {
          if (selectedYear) fetchItems(selectedYear);
          setHistoricoRefresh((n) => n + 1);
        }}
      />
    </div>
  );
}
